import { forwardRef, useState, useRef, useEffect } from 'react';
import { Check, X, Pencil } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface EditableFieldProps {
  value: string;
  onSave: (value: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
  multiline?: boolean;
  className?: string;
}

const EditableField = forwardRef<HTMLDivElement, EditableFieldProps>(
  (
    { value, onSave, type = 'text', placeholder = 'Cliquez pour modifier', multiline = false, className },
    ref
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    useEffect(() => {
      setEditValue(value);
    }, [value]);

    const handleStartEdit = () => {
      setEditValue(value);
      setIsEditing(true);
    };

    const handleSave = () => {
      onSave(editValue);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(value);
      setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !multiline) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    };

    if (isEditing) {
      const inputClasses = cn(
        // `min-w-0 flex-1` : le champ occupe toute la place laissee par les boutons,
        // meme dans une colonne etroite (sinon il s'ecrasait a quelques pixels).
        'w-full min-w-0 flex-1 rounded-lg border border-primary-500 bg-white px-3 py-2 text-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
      );

      return (
        <div ref={ref} className={cn('flex items-start gap-2', className)}>
          {multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(inputClasses, 'min-h-[80px] resize-none')}
              placeholder={placeholder}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className={inputClasses}
              placeholder={placeholder}
            />
          )}
          <div className="flex shrink-0 gap-1">
            <button
              onClick={handleSave}
              className={cn(
                'rounded-lg p-1.5 text-success-600',
                'hover:bg-success-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500'
              )}
              aria-label="Sauvegarder"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancel}
              className={cn(
                'rounded-lg p-1.5 text-danger-600',
                'hover:bg-danger-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500'
              )}
              aria-label="Annuler"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        onClick={handleStartEdit}
        onKeyDown={(e) => e.key === 'Enter' && handleStartEdit()}
        tabIndex={0}
        role="button"
        className={cn(
          'group flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2',
          'hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          'transition-colors',
          className
        )}
      >
        <span className={cn('min-w-0 truncate text-sm', value ? 'text-dark-500' : 'text-neutral-400')} title={value || undefined}>
          {value || placeholder}
        </span>
        <Pencil className="h-3.5 w-3.5 shrink-0 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }
);

EditableField.displayName = 'EditableField';

export { EditableField };
